// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  IconHome,
  IconInference,
  IconJobAutomation,
  IconModel,
  IconRegistries,
  IconRocketLaunch,
} from '@wandb/weave/components/Icon';
import React, {useContext} from 'react';

import {useIsAutomationHubEnabled} from '../../../components/Automations/hooks/useIsAutomationHubEnabled';
import {usePublicQueueAccess} from '../../../components/Launch/hooks';
import {useAccountOrganizationOrPersonalEntityUsingLaunch} from '../../../components/Launch/launchActive';
import {useIsGlobalRegistryEnabled} from '../../../components/Registries/common/hooks';
import {AccountSelectorContext} from '../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../../../components/Search/SearchNav/types';
import {envIsLocal} from '../../../config';
import {
  trackGlobalRegistryClicked,
  trackModelRegistryClicked,
  trackProfileMenuClicked,
} from '../../../util/navigation';
import {useRampFlagInferenceCatalog} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import * as urls from '../../../util/urls';
import {useModelsAccess} from '../../../util/useModelsAccess';
import {useHomePageContext} from '../HomePageContextProvider';
import * as S from './HomePageSidebar.styles';
import {ProjectsList} from './ProjectsList';
import {SidebarLink} from './SidebarLink';
import {SidebarSectionHeader} from './SidebarSectionHeader';
import {TeamsList} from './TeamsList';
import {TrialCard} from './TrialCard';

export const HomePageSidebar = () => {
  const {
    homePageViewerDetailsQuery: {data: viewerData, refetch: refetchViewerData},
  } = useHomePageContext();

  const {enableGlobalRegistry} = useIsGlobalRegistryEnabled();
  const {hasModelsAccess} = useModelsAccess();

  const {isLaunchActive, error: isLaunchActiveError} =
    useAccountOrganizationOrPersonalEntityUsingLaunch();
  const {hasPublicQueueAccess} = usePublicQueueAccess();

  const {selectedAccount} = useContext(AccountSelectorContext);
  const {isAutomationHubEnabled} = useIsAutomationHubEnabled();

  const isPersonalAccount =
    selectedAccount?.accountType === AccountType.Personal;
  const isNonLocalPersonalAccount = !envIsLocal && isPersonalAccount;
  const isInferenceRampEnabled = useRampFlagInferenceCatalog(
    selectedAccount?.name ?? ''
  );
  const isCatalogLinkShown =
    isInferenceRampEnabled || isNonLocalPersonalAccount;

  if (isLaunchActiveError) {
    console.error('Error getting account or personal entity using launch');
  }

  return (
    <div
      data-test="home-page-sidebar"
      className={`[ul>*:p-0] flex h-full flex-col`}>
      <div className="mb-16">
        <SidebarLink
          dataTest="home-page-sidebar-header"
          icon={<IconHome />}
          labelText="Home"
          url={urls.homePage()}
        />
      </div>
      <ProjectsList />
      {viewerData?.username != null && hasModelsAccess && (
        <div className="mb-16">
          <SidebarSectionHeader headerText="Core" />
          {enableGlobalRegistry ? (
            <SidebarLink
              dataTest="global-registry"
              icon={<IconRegistries />}
              labelText="Registry"
              url={urls.registries()}
              onClick={() => {
                trackGlobalRegistryClicked('Home sidebar');
              }}
            />
          ) : (
            <SidebarLink
              dataTest="model-registry"
              icon={<IconModel />}
              labelText="Model registry"
              url={urls.objectRegistry('model')}
              onClick={() => {
                trackModelRegistryClicked('Home sidebar');
              }}
            />
          )}
          {(isLaunchActive || hasPublicQueueAccess) && (
            <SidebarLink
              icon={<IconRocketLaunch />}
              labelText="Launch"
              url={urls.launchOverview()}
            />
          )}
          {isCatalogLinkShown && (
            <SidebarLink
              icon={<IconInference />}
              labelText="Inference"
              url={urls.inferenceModels()}
            />
          )}
          {isAutomationHubEnabled && (
            <SidebarLink
              icon={<IconJobAutomation />}
              labelText="Automations"
              url={urls.homeAutomations()}
            />
          )}
        </div>
      )}
      {viewerData?.username && (
        <div className="mb-16">
          <SidebarSectionHeader headerText="Profile" />
          <SidebarLink
            key={viewerData.username}
            icon={
              <S.AvatarImage
                alt="Home nav profile avatar"
                src={viewerData.photoUrl}
              />
            }
            labelText={viewerData.username}
            url={urls.profilePage(viewerData.username)}
            onClick={() => {
              trackProfileMenuClicked('Home sidebar');
            }}
          />
        </div>
      )}
      {!!viewerData?.teams?.edges?.length && (
        <TeamsList
          name="Teams"
          data={viewerData.teams}
          userQueryRefetch={refetchViewerData}
        />
      )}
      {/* Forces Sticky Footer positioning */}
      <div className="flex flex-auto flex-col justify-end">
        <div className="sticky bottom-16">{!envIsLocal && <TrialCard />}</div>
      </div>
    </div>
  );
};

export default HomePageSidebar; // displayName
